/**
 * 管理端消课批次查询 API（ADM-22）。
 *
 * 仅暴露只读的列表与详情能力；消课事实的更正由后续冲正能力提供。
 */
import { ApiPaths } from './api.config';
import type { Paginated } from './campuses.api';
import { http } from './transport';

export type DeductionBatchOutcome = 'ALL_SUCCESS' | 'PARTIAL_FAILURE' | 'ALL_FAILURE';
/** 本笔扣减所属池（单账户双池：正式课/赠送课；期限卡覆盖行不扣池为 null）。 */
export type DeductLessonKind = 'FORMAL' | 'GIFTED';
export type DeductionItemResult =
  | 'DEDUCTED'
  | 'SKIPPED_NO_BALANCE'
  | 'SKIPPED_ACCOUNT_INVALID'
  | 'REVERSED'
  /** O-14：期限卡覆盖（quantity 恒 0，计入成功人数，不可冲正） */
  | 'PERIOD_COVERED';

/** 批次类型：NORMAL=正常消课（ADM-22）/ MAKEUP=管理端补消（ADM-33），两页口径互斥。 */
export type DeductionBatchType = 'NORMAL' | 'MAKEUP';

/** 消课批次列表查询参数。 */
export interface DeductionBatchListQuery {
  campusId?: string;
  classId?: string;
  dateFrom?: string;
  dateTo?: string;
  teacherId?: string;
  studentId?: string;
  result?: DeductionBatchOutcome;
  /** 批次类型过滤（消课记录页固定 NORMAL，补消页固定 MAKEUP）。 */
  batchType?: DeductionBatchType;
  page?: number;
  pageSize?: number;
}

/** 消课批次列表项。 */
export interface DeductionBatchListItem {
  id: string;
  sessionId: string;
  sessionDate: string;
  startAt: string;
  className: string;
  campusName: string;
  teacherName: string;
  expectedCount: number;
  successCount: number;
  failedCount: number;
  operatorName: string;
  createdAt: string;
  result: DeductionBatchOutcome;
  batchType: DeductionBatchType;
  /** 补消原因（仅 MAKEUP 批次非空）。 */
  reason: string | null;
}

/** 消课批次中的单个学生结果。 */
export interface DeductionBatchDetailItem {
  studentId: string;
  studentName: string;
  /** 本笔扣减所属池（失败行与期限卡覆盖行 null）。 */
  deductLessonKind: DeductLessonKind | null;
  result: DeductionItemResult;
  quantity: number;
  ledgerEntryId: string | null;
  failReason: string | null;
  /** O-14 additive：期限卡覆盖行回指的期限卡凭证 ID。 */
  periodPassId?: string | null;
}

/** 消课批次详情。 */
export interface DeductionBatchDetail extends DeductionBatchListItem {
  skuName: string;
  items: DeductionBatchDetailItem[];
}

function buildListQuery(query: DeductionBatchListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const queryString = params.toString();
  return queryString
    ? `${ApiPaths.deductionBatches.list}?${queryString}`
    : ApiPaths.deductionBatches.list;
}

async function listBatches(
  query: DeductionBatchListQuery = {},
): Promise<Paginated<DeductionBatchListItem>> {
  return http.get<Paginated<DeductionBatchListItem>>(buildListQuery(query));
}

async function getBatch(id: string): Promise<DeductionBatchDetail> {
  return http.get<DeductionBatchDetail>(ApiPaths.deductionBatches.detail(id));
}

/** 消课批次 API 唯一对外入口。 */
export const deductionApi = {
  listBatches,
  getBatch,
};

// ── 6-3 消课明细与冲正（ADM-23，deduction-reversal）────────────────────

import type { ApiError } from './transport';

/** 消课明细列表行（后端 DeductionRecordListItemDto）。 */
export interface DeductionRecordItem {
  id: string;
  batchId: string;
  sessionId: string;
  sessionDate: string;
  startAt: string;
  className: string;
  campusName: string;
  studentId: string;
  studentName: string;
  studentNo: string;
  lessonKind: DeductLessonKind | null;
  result: DeductionItemResult;
  quantity: number;
  failReason: string | null;
  ledgerEntryId: string | null;
  currentAvailable: number | null;
  operatorName: string | null;
  reversalReason: string | null;
  reversalOperatorName: string | null;
  createdAt: string;
  /** O-14 additive：期限卡覆盖行回指的期限卡凭证 ID。 */
  periodPassId?: string | null;
}

/** 冲正结果（后端 ReverseDeductionResultDto）。 */
export interface ReverseDeductionResult {
  reversalEntryId: string;
  restoredQuantity: number;
  availableAfter: number;
  idempotentHit: boolean;
}

/** 消课明细列表查询参数（result 多值逗号分隔）。 */
export interface DeductionRecordListQuery {
  result?: string;
  studentName?: string;
  /** 课次筛选（批次抽屉「去处理」带课次直达，双区块收敛至该课次明细）。 */
  sessionId?: string;
  page?: number;
  pageSize?: number;
}

function buildRecordQuery(query: DeductionRecordListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  return qs ? `${ApiPaths.deductions.list}?${qs}` : ApiPaths.deductions.list;
}

async function listDeductions(
  query: DeductionRecordListQuery = {},
): Promise<Paginated<DeductionRecordItem>> {
  return http.get<Paginated<DeductionRecordItem>>(buildRecordQuery(query));
}

async function getDeduction(id: string): Promise<DeductionRecordItem> {
  return http.get<DeductionRecordItem>(ApiPaths.deductions.detail(id));
}

/**
 * 发起消课冲正。
 *
 * 幂等键由前端生成（design D2：对话框会话内复用，重试同键幂等返回）；
 * 409 业务错误（已被冲正/不可冲正）经 Transport 抛 ApiError（保留稳定 code），由页面映射中文提示。
 */
async function reverseDeduction(
  id: string,
  reason: string,
  idempotencyKey: string,
): Promise<ReverseDeductionResult> {
  return http.post<ReverseDeductionResult>(ApiPaths.deductions.reversal(id), {
    reason,
    idempotencyKey,
  });
}

export type { ApiError };

/** 消课批次/明细/冲正 API 唯一对外入口。 */
export const deductionRecordsApi = {
  listDeductions,
  getDeduction,
  reverseDeduction,
};
